import { PDFDocument, rgb } from '@cantoo/pdf-lib';
import { useEffect, useState } from 'react';

import type { ReactNode } from 'react';
import type { PdfViewerBoundingBox as DatasheetViewerBoundingBox } from './pdf-viewer-schema.js';

export type DatasheetViewerBoundingBoxRendererProps = {
	blobUrl: string;
	boundingBoxes: DatasheetViewerBoundingBox[];
	render: (blobUrl: string) => ReactNode;
	dpi?: number;
};

/**
 * Renders a PDF with bounding boxes drawn on it, showing the plain PDF until the boxed copy is ready. Only the copy
 * for the latest bounding boxes is ever shown, and each copy is freed once a newer one replaces it or the renderer
 * unmounts.
 */
export function DatasheetViewerBoundingBoxRenderer({
	blobUrl,
	boundingBoxes,
	render,
	dpi = 72,
}: DatasheetViewerBoundingBoxRendererProps): ReactNode {
	const [pdf, setPdf] = useState<PDFDocument | undefined>(undefined);
	const [dataUrlWithBoundingBoxes, setDataUrlWithBoundingBoxes] = useState<string | undefined>(undefined);

	useEffect(() => {
		const loadPdf = async () => {
			const pdfBytes = await fetch(blobUrl).then((res) => res.arrayBuffer());

			const pdf = await PDFDocument.load(pdfBytes, { ignoreEncryption: true, password: '' });

			if (pdf.isEncrypted) {
				throw new Error('PDF is password protected.');
			}

			setPdf(pdf);
		};

		void loadPdf();
	}, [blobUrl]);

	useEffect(() => {
		if (!pdf) {
			return;
		}

		let isSuperseded = false;
		const loadPdfWithBoundingBoxes = async () => {
			const pdfWithBoundingBoxes = await pdf.copy();

			for (const boundingBox of boundingBoxes) {
				renderBoundingBox(pdfWithBoundingBoxes, boundingBox, dpi);
			}

			const bytes = await pdfWithBoundingBoxes.save();
			if (isSuperseded) {
				return;
			}

			const blob = new Blob([bytes], { type: 'application/pdf' });
			setDataUrlWithBoundingBoxes(URL.createObjectURL(blob));
		};

		void loadPdfWithBoundingBoxes();
		return () => {
			isSuperseded = true;
		};
	}, [pdf, boundingBoxes, dpi]);

	// The boxed copy shown stays alive until the next one replaces it, so the viewer never loads a revoked URL.
	useEffect(() => {
		if (dataUrlWithBoundingBoxes === undefined) {
			return;
		}
		return () => URL.revokeObjectURL(dataUrlWithBoundingBoxes);
	}, [dataUrlWithBoundingBoxes]);

	return dataUrlWithBoundingBoxes !== undefined ? render(dataUrlWithBoundingBoxes) : render(blobUrl);
}

function renderBoundingBox(pdf: PDFDocument, boundingBox: DatasheetViewerBoundingBox, dpi: number) {
	const pages = pdf.getPages();
	const page = pages[boundingBox.page - 1];

	if (!page) {
		throw new Error(`Page ${boundingBox.page} not found.`);
	}

	// Draw bounding box
	const pageHeight = page.getHeight();
	const rgbColor = rgb(1, 0, 0);

	page.drawRectangle({
		x: boundingBox.ulx * dpi,
		y: pageHeight - boundingBox.lry * dpi,
		width: (boundingBox.lrx - boundingBox.ulx) * dpi,
		height: (boundingBox.lry - boundingBox.uly) * dpi,
		color: rgbColor,
		borderColor: rgbColor,
		borderWidth: 2,
		opacity: 0.2,
	});
}
